import React, { useState, useEffect, useCallback } from 'react';
import ToolLayout from '../components/ToolLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { MapPin, Download, ExternalLink } from 'lucide-react';
import { MAP_SECTIONS, MAP_RESOURCES, buildMapIndexSectionHtml } from '../lib/mapIndex';
import { buildBinderExport, BINDER_EXPORT_EXTENSION } from '../lib/binderExportFormat';
import { toast } from 'sonner';

const STORAGE_KEY = 'map_index_data';

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const USER_AGENT = 'MapIndexTool/1.0 (Preparedness Tools Hub)';

/**
 * Map Index: curated map resources with links and instructions.
 * Optional address lookup adds "your location" and coordinates to the export for use in any map.
 */
const MapIndexTool = () => {
  const [addressInput, setAddressInput] = useState('');
  const [location, setLocation] = useState(null); // { address, lat, lon, displayName }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.lat === 'number' && typeof parsed.lon === 'number') {
          setLocation({
            address: parsed.address ?? '',
            lat: parsed.lat,
            lon: parsed.lon,
            displayName: parsed.displayName ?? parsed.address ?? '',
          });
        }
      }
    } catch (_) {
      // ignore
    }
  }, []);

  const persist = useCallback((data) => {
    if (!data) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (_) {}
  }, []);

  const handleLookup = async () => {
    const query = (addressInput || '').trim();
    if (!query) {
      toast.error('Enter an address to look up.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        format: 'json',
        q: query,
        limit: '1',
      });
      const response = await fetch(`${NOMINATIM_URL}?${params}`, {
        headers: { 'User-Agent': USER_AGENT },
      });
      if (!response.ok) throw new Error('Search failed');
      const data = await response.json();
      if (data && data.length > 0) {
        const result = data[0];
        const lat = parseFloat(result.lat);
        const lon = parseFloat(result.lon);
        const displayName = result.display_name || query;
        const next = { address: query, lat, lon, displayName };
        setLocation(next);
        persist(next);
        toast.success(`Found: ${displayName}`);
        setAddressInput('');
      } else {
        setError('Location not found. Try a different address.');
        toast.error('Location not found');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to search location. Please try again.');
      toast.error('Failed to search location');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const html = buildMapIndexSectionHtml(location);
    const sections = [{ name: 'Map Index', content: html }];
    const payload = buildBinderExport('map-index', 'Map Index', sections);
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Map_Index_${new Date().toISOString().split('T')[0]}${BINDER_EXPORT_EXTENSION}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Export downloaded. Upload this file in Build your binder.');
  };

  return (
    <ToolLayout
      title="Map Index"
      description="Curated map resources with links and instructions. Optionally add your location so your binder includes coordinates to use in any map."
      icon={MapPin}
      guide={{
        intro: 'Curated map resources so you have navigation options when GPS is down.',
        steps: [
          'Browse free map resources (OpenTopoMap, USGS, OpenStreetMap, etc.).',
          'Follow the instructions for each resource.',
          'Optionally enter your address to save coordinates for your binder.',
          'Export for your binder.',
        ],
      }}
    >
      <div className="max-w-2xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              Your location (optional)
            </CardTitle>
            <CardDescription>
              Look up an address to include your location and coordinates in the export. You can then copy the coordinates into any map site’s search box.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <div className="flex-1 min-w-[200px]">
                <Label htmlFor="map-index-address" className="sr-only">
                  Address
                </Label>
                <Input
                  id="map-index-address"
                  value={addressInput}
                  onChange={(e) => setAddressInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                  placeholder="e.g. 123 Main St, City, State"
                  disabled={loading}
                />
              </div>
              <Button onClick={handleLookup} disabled={loading}>
                {loading ? 'Looking up…' : 'Look up'}
              </Button>
            </div>
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
            {location && (
              <div className="rounded-md border bg-muted/30 p-3 text-sm">
                <div>
                  <span className="font-medium text-muted-foreground">Location: </span>
                  {location.displayName || location.address || '—'}
                </div>
                <div className="mt-1 font-mono text-muted-foreground">
                  {location.lat.toFixed(5)}, {location.lon.toFixed(5)}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  These coordinates will appear in your export so you can paste them into any map.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {MAP_SECTIONS.map((sec) => {
          const resourcesInSection = MAP_RESOURCES.filter((r) => r.section === sec.id);
          if (resourcesInSection.length === 0) return null;
          return (
            <Card key={sec.id}>
              <CardHeader>
                <CardTitle>{sec.title}</CardTitle>
                <CardDescription>{sec.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {resourcesInSection.map((res) => {
                  const act = res.activity;
                  return (
                    <div key={res.id} className="rounded-md border bg-card p-4 text-sm space-y-3">
                      <div className="font-medium">{res.name}</div>
                      <p className="text-muted-foreground">{res.description}</p>
                      <a
                        href={res.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        {res.url} <ExternalLink className="h-3 w-3" />
                      </a>
                      {act && (
                        <>
                          <div>
                            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Why</span>
                            <p className="mt-1 text-foreground">{act.why}</p>
                          </div>
                          {act.steps && act.steps.length > 0 && (
                            <div>
                              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Steps</span>
                              <ol className="mt-1 list-decimal list-inside space-y-1 text-foreground">
                                {act.steps.map((step, i) => (
                                  <li key={i}>{step}</li>
                                ))}
                              </ol>
                            </div>
                          )}
                          <div>
                            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">What you get</span>
                            <p className="mt-1 text-foreground">{act.outcome}</p>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          );
        })}
        <Card>
          <CardContent className="pt-6">
            <Button onClick={handleExport} variant="default">
              <Download className="h-4 w-4 mr-2" />
              Export for binder
            </Button>
            <p className="text-muted-foreground text-sm mt-2">
              Exports your location (if set) plus all sections above—links, Why/Steps/What you get—so the binder page is a full reference when offline.
            </p>
          </CardContent>
        </Card>
      </div>
    </ToolLayout>
  );
};

export default MapIndexTool;
